
import { defineComponent } from '@vue/composition-api';
import { useApiV3 } from '../../../composables/apiV3';
import IllinoisTabBar from '../components/TabBar.vue';
import OddsEventsGroup from '../components/OddsComponents/OddsEventsGroup.vue';
import icons from '../components/icons.vue';
import { GenericOption } from '../../../../types/components';
import {
  Category,
  CategoriesApiResponse,
  Tournament,
  TournamentsForSportCategoryResponse,
  TournamentPreviewResponse,
  PreviewEvent,
  PreviewEventGroup,
  MarketGroup,
  Market,
  PreviewOdd,
  PreviewOutcome
} from '../../../../types/sport';
import OddsExpandibleCard from '../components/OddsComponents/OddsExpandibleCard.vue';
import { formatDateAndTime } from '@/composables/utils';
import { getCorrectData, addOddToCartV3FromSport } from '@/composables/useOdds';
import ChangePageModal from '../components/ChangePageModal.vue';

interface EnhancedEvent {
  id: number;
  home: string;
  away: string;
  scheduled: string;
  outcomeValues: string[];
}

export default defineComponent({
  name: 'TournamentPage',
  props: {
    categoryId: {
      type: String,
      required: true
    },
    sportId: {
      type: String,
      required: true
    },
    tournamentId: {
      type: String,
      required: true
    }
  },
  components: {
    IllinoisTabBar,
    OddsEventsGroup,
    OddsExpandibleCard,
    icons,
    ChangePageModal
  },
  data() {
    return {
      categorySelectedId: this.categoryId as string | null,

      showCategoriesTitleMenu: false,
      isTitleMenuLoading: false,
      openTitleMenu: false,

      loading: true,
      error: null as string | null,
      sportName: null as string | null,

      tournamentsByCategory: {} as Record<number, Tournament[]>,
      isLoadingTournaments: {} as Record<number, boolean>,
      expandedCategories: new Set<number>(),
      tournamentEvents: {} as Record<string, PreviewEventGroup[]>,
      selectedMarketGroup: null as string | null,

      selectedMarketGroupMenu: null as string | null,

      selectedMarket: null as string | null,
      activeSpread: null as string | null,
      correctMarket: '5185578776' as string | null,
      correctMarket2: '7836838694' as string | null,
      showMarketMenu: false,
      viewportWidth: (typeof window !== 'undefined'
        ? window.innerWidth
        : 1024) as number,
      menuTop: '0px' as string,
      isMarketLoading: false as boolean,
      marketSearchQuery: '' as string
    };
  },
  computed: {
    windowWidth() {
      return this.viewportWidth;
    },
    isDesktop(): boolean {
      return this.windowWidth >= 768;
    },
    isMarketGroupsReady(): boolean {
      return Array.isArray(this.marketGroups) && this.marketGroups.length > 0;
    },
    isMarketsReady(): boolean {
      return (
        Array.isArray(this.marketTabItems) && this.marketTabItems.length > 0
      );
    },
    skeletonGroup(): any {
      return { scheduled: '', events: [] } as any;
    },
    skeletonEvent(): any {
      return { home: '', away: '', scheduled: '' } as any;
    },
    skeletonOdds(): any {
      return [[], [], []] as any;
    },
    tournamentName(): string {
      return (
        this.$store.getters['IllinoisSport/getTournamentsByCategory'](
          this.categoryId
        )?.find(
          tournament => tournament.tournamentId.toString() === this.tournamentId
        )?.name || ''
      );
    },
    categoryName(): string {
      return (
        this.$store.getters['IllinoisSport/getCategories'][this.sportId].find(
          category => category.categoryId.toString() === this.categoryId
        )?.name || ''
      );
    },
    categorySelectedName(): string {
      return (
        this.$store.getters['IllinoisSport/getCategories'][this.sportId].find(
          category => category.categoryId.toString() === this.categorySelectedId
        )?.name || ''
      );
    },
    tournaments(): Tournament[] {
      return (
        this.$store.getters['IllinoisSport/getTournamentsByCategory'](
          this.categorySelectedId
        ) || []
      );
    },
    previewEventsGroup(): PreviewEventGroup[] {
      if (this.activeMarket === 'preview') {
        return this.$store.getters['IllinoisSport/getPreviewByTournament'](
          this.tournamentId
        );
      }
      return this.$store.getters['IllinoisSport/getEventsByTournament'](
        this.tournamentId
      );
    },
    activeMarketGroup(): string | null {
      return (
        this.selectedMarketGroup || this.marketGroupsTabItems[0]?.value || null
      );
    },
    activeMarket(): string | null {
      return this.selectedMarket || this.marketTabItems[0]?.value || null;
    },
    activeMarketGroupMenu(): string | null {
      return (
        this.selectedMarketGroupMenu ||
        this.activeMarketGroup ||
        this.marketGroupsTabItemsMenu[0]?.value ||
        null
      );
    },
    eventsFilteredMarket(): PreviewEventGroup[] {
      if (!this.previewEventsGroup || this.previewEventsGroup.length === 0) {
        return [];
      }

      // Se è il market "preview", mostra tutti gli eventi con tutte le odds
      if (this.activeMarket === 'preview') {
        return this.previewEventsGroup;
      }

      // Filtriamo i gruppi di eventi e le loro odds in base al mercato attivo
      return this.previewEventsGroup.map(group => {
        // Per ogni gruppo, filtriamo gli eventi che hanno almeno una odd
        const filteredEvents = group.events
          .filter(event => {
            // Verifica se l'evento ha odds per il mercato selezionato
            if (
              !this.activeMarket ||
              !event.odds ||
              !Array.isArray(event.odds)
            ) {
              return false;
            }
            return event.odds.some(odd => odd.id === this.activeMarket);
          })
          .map(event => {
            // Filtra le odds dell'evento per il mercato selezionato
            const filteredOdds = event.odds.filter(
              odd => odd.id === this.activeMarket
            );

            // Restituisci una copia dell'evento con le odds filtrate
            return {
              ...event,
              odds: filteredOdds
            };
          });

        // Restituisci una copia del gruppo con gli eventi filtrati
        return {
          scheduled: group.scheduled,
          events: filteredEvents
        };
      });
    },
    eventsFilteredMarketWithoutClosedOutcomes(): PreviewEventGroup[] {
      return this.eventsFilteredMarket.map(group => {
        return {
          ...group,
          events: group.events.map(event => {
            return {
              ...event,
              odds: event.odds.map(odd => {
                // Raggruppa gli outcome di questa singola odd per specifier
                const outcomesBySpecifier: Record<string, PreviewOutcome[]> =
                  {};
                odd.outcome.forEach(outcome => {
                  const specifier = outcome.specifier || '';
                  if (!outcomesBySpecifier[specifier]) {
                    outcomesBySpecifier[specifier] = [];
                  }
                  outcomesBySpecifier[specifier].push(outcome);
                });

                // Trova gli specifier che hanno almeno un outcome valido
                // (value diverso da null/vuoto OPPURE status diverso da 2)
                const validSpecifiers = new Set<string>();
                Object.keys(outcomesBySpecifier).forEach(specifier => {
                  const hasValidOutcome = outcomesBySpecifier[specifier].some(
                    outcome =>
                      outcome.outcome_odd_value != null &&
                      outcome.outcome_odd_value !== '' &&
                      outcome.status !== 2
                  );
                  if (hasValidOutcome) {
                    validSpecifiers.add(specifier);
                  }
                });

                // Filtra gli outcome rimuovendo quelli con specifier non validi
                const filteredOutcomes = odd.outcome.filter(outcome => {
                  const specifier = outcome.specifier || '';
                  return validSpecifiers.has(specifier);
                });

                return {
                  ...odd,
                  outcome: filteredOutcomes
                };
              })
            };
          })
        };
      });
    },
    eventsMarketFilteredSpread(): PreviewEventGroup[] {
      return this.eventsFilteredMarket.map(group => {
        return {
          ...group,
          events: group.events.map(event => {
            return {
              ...event,
              odds: event.odds.map(odd => {
                const allOutcomes = Array.isArray(odd.outcome)
                  ? odd.outcome
                  : [];

                // Se è preview, non filtrare per lo spread
                if (this.activeMarket === 'preview') {
                  return odd;
                }

                // Se il mercato ha specifier ed è selezionato uno spread, filtra
                if (odd.odd_with_specifier && this.activeSpread) {
                  const filteredOutcomes = allOutcomes.filter(
                    outcome => outcome.specifier === this.activeSpread
                  );

                  // Se non ci sono outcome per lo spread selezionato, genera placeholder
                  if (filteredOutcomes.length === 0) {
                    // Prova a capire quante colonne dovrebbe avere questo mercato
                    // Raggruppa per specifier per ottenere il numero tipico di outcomes
                    const outcomesBySpecifier: Record<string, number> = {};
                    for (const o of allOutcomes) {
                      const key = o.specifier || '_';
                      outcomesBySpecifier[key] =
                        (outcomesBySpecifier[key] || 0) + 1;
                    }

                    const counts = Object.values(outcomesBySpecifier);
                    const defaultCount =
                      counts.length > 0
                        ? Math.max(...counts)
                        : allOutcomes.length || 0;

                    // Usa un campione di outcomes ordinati per sort per mantenere l'ordine delle colonne
                    const sample = [...allOutcomes]
                      .sort((a, b) => (a.sort || 0) - (b.sort || 0))
                      .slice(0, defaultCount);

                    const placeholderOutcomes = sample.map(s => ({
                      ...s,
                      // Forza lo spread selezionato per coerenza
                      specifier: this.activeSpread as string,
                      // Rendi il valore quota assente così che OddsOutcomes mostri il lock
                      outcome_odd_value: ''
                    }));

                    return {
                      ...odd,
                      outcome: placeholderOutcomes
                    };
                  }

                  return {
                    ...odd,
                    outcome: filteredOutcomes
                  };
                }

                // Mercato senza specifier o senza spread attivo: ritorna invariato
                return odd;
              })
            };
          })
        };
      });
    },
    eventsMarketFilteredSpreadActive(): PreviewEventGroup[] {
      const groups = this.eventsMarketFilteredSpread || [];
      return groups
        .map(group => ({
          ...group,
          events: (Array.isArray(group.events) ? group.events : []).filter(
            (event: PreviewEvent) => this.hasActiveOutcomesForEvent(event)
          )
        }))
        .filter(
          group => Array.isArray(group.events) && group.events.length > 0
        );
    },
    eventsMarketFilteredSpreadActiveWithColumns(): PreviewEventGroup[] {
      // Restituisce i dati RAW senza trasformazioni
      // La trasformazione avviene in OddsEvent.vue
      return this.eventsMarketFilteredSpreadActive;
    },
    columnsForTournament(): string[] {
      const groups = this.eventsMarketFilteredSpreadActive || [];
      if (groups.length === 0) {
        return [];
      }

      // Raccogli tutti gli eventi da tutti i gruppi
      const allEvents: PreviewEvent[] = [];
      groups.forEach(group => {
        if (Array.isArray(group.events)) {
          allEvents.push(...group.events);
        }
      });

      if (allEvents.length === 0) {
        return [];
      }

      // Determina il tipo di struttura guardando il primo evento con odds valide
      let structureType: 'multiple-odds' | 'single-odd' | null = null;
      let firstEventWithOdds: PreviewEvent | null = null;

      for (const event of allEvents) {
        if (event.odds && Array.isArray(event.odds) && event.odds.length > 0) {
          firstEventWithOdds = event;
          structureType =
            event.odds.length > 1 ? 'multiple-odds' : 'single-odd';
          break;
        }
      }

      if (!firstEventWithOdds || !structureType) {
        return [];
      }

      // Se odds.length > 1: i nomi delle colonne sono i marketName di ogni odd
      if (structureType === 'multiple-odds') {
        const columnsSet = new Set<string>();
        const columnsOrder: string[] = [];

        // Prima passata: raccogli tutte le colonne mantenendo l'ordine del primo evento
        if (firstEventWithOdds.odds) {
          firstEventWithOdds.odds.forEach(odd => {
            const marketName = odd.marketName || '';
            if (marketName && !columnsSet.has(marketName)) {
              columnsSet.add(marketName);
              columnsOrder.push(marketName);
            }
          });
        }

        // Seconda passata: aggiungi colonne da altri eventi che non sono già presenti
        allEvents.forEach(event => {
          if (event.odds && Array.isArray(event.odds)) {
            event.odds.forEach(odd => {
              const marketName = odd.marketName || '';
              if (marketName && !columnsSet.has(marketName)) {
                columnsSet.add(marketName);
                columnsOrder.push(marketName);
              }
            });
          }
        });

        return columnsOrder;
      }

      // Se odds.length === 1: i nomi delle colonne sono i nomi degli outcomes
      const firstOdd = firstEventWithOdds.odds[0];
      if (!firstOdd || !firstOdd.outcome || !Array.isArray(firstOdd.outcome)) {
        return [];
      }

      const columnsSet = new Set<string>();
      const columnsOrder: string[] = [];

      // Prima passata: raccogli tutte le colonne mantenendo l'ordine del primo evento
      const firstOutcomes = firstOdd.outcome as PreviewOutcome[];
      firstOutcomes.forEach(outcome => {
        const columnName = outcome.outcome_name || outcome.outcome_alias || '';
        if (columnName && !columnsSet.has(columnName)) {
          columnsSet.add(columnName);
          columnsOrder.push(columnName);
        }
      });

      // Seconda passata: aggiungi colonne da altri eventi che non sono già presenti
      allEvents.forEach(event => {
        if (
          event.odds &&
          Array.isArray(event.odds) &&
          event.odds.length === 1 &&
          event.odds[0].outcome &&
          Array.isArray(event.odds[0].outcome)
        ) {
          const outcomes = event.odds[0].outcome as PreviewOutcome[];
          outcomes.forEach(outcome => {
            const columnName =
              outcome.outcome_name || outcome.outcome_alias || '';
            if (columnName && !columnsSet.has(columnName)) {
              columnsSet.add(columnName);
              columnsOrder.push(columnName);
            }
          });
        }
      });

      return columnsOrder;
    },
    isExpandibleCardMarket(): boolean {
      // Se siamo in preview, usa OddsEventsGroup
      if (this.activeMarket === 'preview') {
        return false;
      }

      // Se è uno dei correctMarket, usa OddsExpandibleCard con multiple-modes (gestito dalla prima condizione)
      if (
        String(this.activeMarket) === this.correctMarket ||
        String(this.activeMarket) === this.correctMarket2
      ) {
        return false;
      }

      const groups = this.eventsMarketFilteredSpread;
      if (!groups || groups.length === 0) return false;

      // Determina il threshold basato sulla presenza di spreadOptions
      const hasSpreadOptions =
        Array.isArray(this.spreadOptions) && this.spreadOptions.length > 0;
      const threshold = hasSpreadOptions ? 1 : 4;

      for (const group of groups) {
        if (!group || !Array.isArray(group.events)) continue;
        for (const event of group.events) {
          if (!event?.odds || !Array.isArray(event.odds)) continue;

          // Condizione 1: Controlla se almeno una odd ha odd_with_specifier: true
          if (event.odds.some(odd => odd.odd_with_specifier === true)) {
            return true;
          }

          // Condizione 2: Controlla se il numero di outcome supera la soglia
          const firstOdd = event.odds[0];
          if (firstOdd && Array.isArray(firstOdd.outcome)) {
            if (firstOdd.outcome.length >= threshold) {
              return true;
            }
          }
        }
      }

      return false;
    },
    allSports() {
      return this.$store.getters['IllinoisSport/getSports'];
    },

    platformId() {
      return this.$store.getters['settings/getPlatformId'];
    },
    marketGroups() {
      const groups = this.$store.getters[
        'IllinoisSport/getMarketGroupsForTournament'
      ](this.tournamentId);

      return groups.map(group => {
        if (group._id && group._id.includes('Preview') && group.markets) {
          const markets = [...group.markets];
          const previewMarket = {
            id: 'preview',
            unique_id: 'preview',
            sort: '0',
            sortMobile: '0',
            name: 'Preview'
          };
          markets.unshift(previewMarket);

          return {
            ...group,
            markets
          };
        }
        return group;
      });
    },
    marketGroupsTabItems() {
      return this.marketGroups && this.marketGroups.length > 1
        ? this.marketGroups.map(group => ({
            label: group.groupName,
            value: group._id
          }))
        : this.marketGroups.map(group => ({
            label: group.groupName,
            value: group._id
          }));
    },
    marketGroupsTabItemsMenu() {
      if (!this.marketGroups || this.marketGroups.length === 0) return [];
      // Usa una copia per evitare mutazioni sullo stato originale
      const arr = this.marketGroups.slice();
      return arr.map(group => ({
        label: group.groupName,
        value: group._id
      }));
    },
    marketTabItems() {
      return (
        this.marketGroups
          .find(group => group._id === this.activeMarketGroup)
          ?.markets.map(market => ({
            label: market.name,
            value: market.id
          })) || []
      );
    },
    marketTabItemsMenu() {
      return (
        this.marketGroups
          .find(group => group._id === this.activeMarketGroupMenu)
          ?.markets.map(market => ({
            label: market.name,
            value: market.id
          })) || []
      );
    },
    filteredMarketTabItemsMenu(): GenericOption[] {
      if (!this.marketSearchQuery || this.marketSearchQuery.trim() === '') {
        return this.marketTabItemsMenu;
      }
      const query = this.marketSearchQuery.toLowerCase().trim();
      // Se c'è una query, cerca in tutti i market di tutti i gruppi
      const allMarkets: GenericOption[] = [];
      this.marketGroups.forEach(group => {
        if (group.markets && Array.isArray(group.markets)) {
          group.markets.forEach(market => {
            allMarkets.push({
              label: market.name,
              value: market.id
            });
          });
        }
      });
      // Rimuovi duplicati basandosi sul value
      const uniqueMarkets = allMarkets.reduce(
        (acc: GenericOption[], current: GenericOption) => {
          const isDuplicate = acc.find(item => item.value === current.value);
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        },
        []
      );
      // Filtra per la query
      return uniqueMarkets.filter(market =>
        market.label.toLowerCase().includes(query)
      );
    },
    spreadOptions(): GenericOption[] {
      // Verifica se abbiamo eventi e quote validi
      if (
        !this.eventsFilteredMarket ||
        !this.eventsFilteredMarket[0] ||
        !this.eventsFilteredMarket[0]?.events ||
        !this.eventsFilteredMarket[0]?.events[0] ||
        !this.eventsFilteredMarket[0]?.events[0]?.odds ||
        !this.eventsFilteredMarket[0]?.events[0]?.odds[0] ||
        !this.eventsFilteredMarket[0]?.events[0]?.odds[0]?.outcome
      ) {
        return [];
      }

      // Estrai tutti gli specifier dagli outcome
      const firstOdd = this.eventsFilteredMarket[0]?.events[0]?.odds[0];
      const marketName = firstOdd?.marketName || '';

      // Verifica che outcome sia un array
      if (!Array.isArray(firstOdd?.outcome)) {
        return [];
      }

      const allSpecifiers: GenericOption[] = firstOdd.outcome
        .map((outcome: PreviewOutcome) => {
          if (!outcome || !outcome.specifier) {
            return null;
          }
          return {
            label: marketName + ' ' + outcome.specifier,
            value: outcome.specifier
          };
        })
        .filter(item => item !== null) as GenericOption[];

      // Rimuovi i duplicati basandosi sulla proprietà value
      const uniqueSpecifiers = allSpecifiers.reduce(
        (acc: GenericOption[], current: GenericOption) => {
          // Controlla se esiste già un elemento con lo stesso value nell'accumulatore
          const isDuplicate = acc.find(item => item.value === current.value);
          // Se non è un duplicato, aggiungilo all'accumulatore
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        },
        []
      );

      return uniqueSpecifiers.length > 0 ? uniqueSpecifiers : null;
    },
    categories() {
      return this.$store.getters['IllinoisSport/getCategories'][this.sportId];
    }
  },

  watch: {
    showMarketMenu: {
      handler(open: boolean) {
        if (open) {
          this.$nextTick(() => {
            const btnEl = this.$refs.marketMenuButton as
              | HTMLElement
              | undefined;
            if (btnEl) {
              const rect = btnEl.getBoundingClientRect();
              // fixed uses viewport coords
              this.menuTop = `${Math.max(0, Math.round(rect.top))}px`;
            }
            // Focus sull'input di ricerca
            const searchInput = this.$refs.marketSearchInput as
              | HTMLInputElement
              | undefined;
            if (searchInput) {
              searchInput.focus();
            }
          });
        } else {
          this.marketSearchQuery = '';
        }
      }
    },
    sportId: {
      handler(newSportId, oldSportId) {
        if (newSportId !== oldSportId) {
          // Resettiamo i dati
          this.categories = [];
          this.tournamentsByCategory = {};
          this.isLoadingTournaments = {};
          this.expandedCategories.clear();

          // Ricarichiamo i dati con il nuovo sportId
          this.fetchSportName();
          this.fetchTournamentData();
        }
      }
    },
    tournamentId: {
      handler(newTournamentId, oldTournamentId) {
        this.openTitleMenu = false;
        if (newTournamentId !== oldTournamentId) {
          // Ricarichiamo le quote del torneo
          this.fetchTournamentData();
        }
      }
    },
    activeMarket: {
      async handler(newMarket, oldMarket) {
        if (newMarket !== oldMarket && newMarket) {
          // Se è il market "preview", usa previewByTournament
          if (newMarket === 'preview') {
            const previewData = this.$store.getters[
              'IllinoisSport/getPreviewByTournament'
            ](this.tournamentId);

            if (!previewData || previewData.length === 0) {
              this.isMarketLoading = true;
              try {
                await this.$store.dispatch(
                  'IllinoisSport/fetchPreviewByTournament',
                  {
                    sportId: this.sportId,
                    categoryId: this.categoryId,
                    tournamentId: this.tournamentId
                  }
                );
              } finally {
                this.isMarketLoading = false;
              }
            }
            return;
          }

          // Verifica se abbiamo già le quote per questo mercato
          let hasMarketData = false;

          // Controlla in tutti i gruppi di eventi
          for (const group of this.previewEventsGroup) {
            for (const event of group.events) {
              // Cerca odds per il mercato attivo
              const marketOdds =
                event.odds && this.activeMarket
                  ? event.odds.filter(odd => odd.id === this.activeMarket)
                  : [];
              if (marketOdds.length > 0) {
                hasMarketData = true;
                break;
              }
            }
            if (hasMarketData) break;
          }

          // Se non abbiamo dati per questo mercato, li carichiamo
          if (!hasMarketData && this.activeMarket) {
            this.isMarketLoading = true;
            try {
              await this.$store.dispatch(
                'IllinoisSport/fetchTournamentOddMarket',
                {
                  sportId: this.sportId,
                  categoryId: this.categoryId,
                  tournamentId: this.tournamentId,
                  marketId: this.activeMarket,
                  groupId: this.activeMarketGroup
                }
              );
            } finally {
              this.isMarketLoading = false;
            }
          } else {
            this.isMarketLoading = false;
          }
        }
      }
    },
    /* marketGroupsTabItems: {
      handler(newMarketGroups, oldMarketGroups) {
        if (newMarketGroups !== oldMarketGroups) {
          console.log('Market groups cambiato:', newMarketGroups);
          const firstMarketGroup = newMarketGroups[0];
          this.selectedMarketGroup = firstMarketGroup?._id;
          this.activeMarket = firstMarketGroup?.markets?.[0]?.id;
        }
      }
    }, */
    /*  marketGroupsTabItemsMenu: {
      handler(newMarketGroupsMenu, oldMarketGroupsMenu) {
        if (newMarketGroupsMenu !== oldMarketGroupsMenu) {
          console.log('Market groups menu cambiato:', newMarketGroupsMenu);
          const firstMarketGroup = newMarketGroupsMenu[0];
          this.activeMarketGroupMenu = firstMarketGroup?._id;
          this.activeMarketMenu = firstMarketGroup?.markets?.[0]?.id;
        }
      }
    }, */
    activeMarketGroup: {
      handler(newMarketGroup, oldMarketGroup) {
        if (newMarketGroup !== oldMarketGroup) {
          const group = this.marketGroups.find(
            group => group._id === newMarketGroup
          );
          const firstMarketId =
            group?.markets && group.markets.length > 0
              ? group.markets[0].id
              : null;
          this.activeMarket = firstMarketId;
        }
      }
    },

    spreadOptions: {
      handler(newSpreadOptions) {
        if (newSpreadOptions && newSpreadOptions.length > 0) {
          this.activeSpread = newSpreadOptions[0].value;
        } else {
          this.activeSpread = undefined;
        }
      },
      immediate: true
    }
  },
  async mounted() {
    window.parent.postMessage(
      {
        type: 'illinoisTournamentOpened',
        sportId: this.sportId,
        categoryId: this.categoryId,
        tournamentId: this.tournamentId
      },
      '*'
    );
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize, { passive: true });
    }
    await Promise.all([
      this.fetchSportName(),
      this.fetchTournamentData(),
      this.$store.dispatch('IllinoisSport/fetchCategoriesIfNeeded', {
        sportId: this.sportId
      }),
      this.$store.dispatch(
        'IllinoisSport/fetchTournamentsForCategoryIfNeeded',
        {
          sportId: this.sportId,
          categoryId: this.categoryId
        }
      )
    ]);
  },

  methods: {
    // Espongo la funzione importata per l'uso nel template
    getCorrectData,
    formatDateAndTime,
    addOddToCartV3FromSport,
    hasActiveOutcomesForEvent(event: PreviewEvent): boolean {
      if (!event || !Array.isArray(event.odds)) return false;
      return event.odds.some(odd =>
        Array.isArray(odd?.outcome)
          ? odd.outcome.some(o => o.status === 1)
          : false
      );
    },
    onResize() {
      this.viewportWidth = window.innerWidth;
    },
    goBack() {
      this.$router.back();
    },
    hasGroupData(group: any): boolean {
      if (!group || !Array.isArray(group.events) || group.events.length === 0)
        return false;
      return group.events.some((e: any) => {
        const oddsArr = Array.isArray(e?.odds) ? e.odds : [];
        const first = oddsArr[0];
        const outcomes = Array.isArray(first?.outcome) ? first.outcome : [];
        return outcomes.length > 0;
      });
    },
    fetchSportName() {
      if (this.allSports && this.allSports.length > 0) {
        // Cerchiamo lo sport con l'ID corrispondente
        const sport = this.allSports.find(
          s => s.sportId.toString() === this.sportId
        );
        if (sport) {
          this.sportName = sport.name;
        } else {
          this.$store.dispatch('IllinoisSport/fetchSportsIfNeeded').then(() => {
            // Riprova dopo che gli sport sono stati caricati
            const sportAfterFetch = this.allSports.find(
              s => s.sportId.toString() === this.sportId
            );
            if (sportAfterFetch) {
              this.sportName = sportAfterFetch.name;
            }
          });
        }
      } else {
        // Se non ci sono sport, facciamo il dispatch dell'azione per caricarli
        this.$store.dispatch('IllinoisSport/fetchSportsIfNeeded').then(() => {
          // Riprova dopo che gli sport sono stati caricati
          const sport = this.allSports.find(
            s => s.sportId.toString() === this.sportId
          );
          if (sport) {
            this.sportName = sport.name;
          }
        });
      }
    },
    t(key: string, values?: Record<string, unknown> | any[]) {
      return this.$t(key, values) as string;
    },

    async fetchTournamentData() {
      this.loading = true;
      this.error = null;
      this.selectedMarketGroup = null;
      this.selectedMarketGroupMenu = null;
      this.selectedMarket = null;
      this.selectedMarketMenu = null;

      try {
        // Eseguiamo entrambe le chiamate API in parallelo
        const [oddsResponse, marketGroupsResponse] = await Promise.all([
          this.$store.dispatch('IllinoisSport/fetchEventsByTournament', {
            sportId: this.sportId,
            categoryId: this.categoryId,
            tournamentId: this.tournamentId
          }),
          this.$store.dispatch(
            'IllinoisSport/fetchMarketGroupsForTournamentIfNeeded',
            {
              sportId: this.sportId,
              categoryId: this.categoryId,
              tournamentId: this.tournamentId
            }
          )
        ]);

        // I dati sono già stati impostati dalle singole funzioni
      } catch (err) {
        this.error = 'Errore nel caricamento dei dati del torneo';
        console.error('Errore nel caricamento dei dati del torneo:', err);
      } finally {
        this.loading = false;
      }
    },

    /* async fetchTournamentOdds() {
      try {
        console.log(`Caricamento odds per il torneo ${this.tournamentId}...`);
        const api = useApiV3();
        const response = await api.getPreviewForTournament(
          this.sportId.toString(),
          this.categoryId.toString(),
          this.tournamentId.toString()
        );

        if (response && response.events) {
          console.log('Quote torneo caricate:', response);
          // Salva gli eventi nel formato corretto
          this.tournamentName = response.tournamentName;
          this.previewEventsGroup = response.events;
          this.categorySelectedName = response.categoryName;
          this.categorySelectedId = response.categoryId;

          // Aggiorna anche il nome dello sport se non è già impostato
          if (!this.sportName && response.sportName) {
            this.sportName = response.sportName;
          }

          return response;
        } else {
          throw new Error(
            'Errore nel caricamento delle quote: formato non valido'
          );
        }
      } catch (err) {
        console.error('Errore nella chiamata API odds:', err);
        throw err;
      }
    }, */

    /*  async fetchTournamentOddsMarket(marketId: string) {
      try {
        // Verifica che marketId sia valido
        if (!marketId) {
          console.error(
            'fetchTournamentOddsMarket chiamato con marketId non valido:',
            marketId
          );
          return;
        }

        console.log(
          `Caricamento odds per il torneo ${this.tournamentId} e market ${marketId} e market group ${this.activeMarketGroup}...`
        );
        const api = useApiV3();
        const response = await api.getPreviewForTournamentMarket(
          this.sportId,
          this.categoryId,
          this.tournamentId,
          marketId,
          this.activeMarketGroup
        );

        if (response && response.events) {
          console.log('Quote torneo caricate per il market:', response);
          // Log dettagliato per il debug
          console.log('Market corrente:', this.activeMarket);
          console.log('Market ID richiesto:', marketId);

          // Iteriamo su ogni gruppo di eventi nella risposta
          response.events.forEach((responseGroup: PreviewEventGroup) => {
            // Cerchiamo il gruppo corrispondente per data schedulata

            let existingGroup = this.previewEventsGroup.find(group => {
              const groupDate = new Date(group.scheduled);
              const responseDate = new Date(responseGroup.scheduled);
              return (
                groupDate.getFullYear() === responseDate.getFullYear() &&
                groupDate.getMonth() === responseDate.getMonth() &&
                groupDate.getDate() === responseDate.getDate()
              );
            });

            // Se non esiste un gruppo con questa data, lo creiamo
            if (!existingGroup) {
              existingGroup = {
                scheduled: responseGroup.scheduled,
                events: []
              };
              this.previewEventsGroup.push(existingGroup);
            }

            // Iteriamo sugli eventi nel gruppo di risposta
            responseGroup.events.forEach((responseEvent: PreviewEvent) => {
              // Cerchiamo l'evento corrispondente nel gruppo esistente
              let existingEvent = existingGroup.events.find(
                event => event.id === responseEvent.id
              );

              // Se l'evento non esiste, lo aggiungiamo
              if (!existingEvent) {
                existingEvent = { ...responseEvent };
                existingGroup.events.push(existingEvent);
              } else {
                // Se l'evento esiste, aggiorniamo le sue quote
                // Cerchiamo tutte le quote disponibili dell'evento
                const marketOdds = responseEvent.odds;

                // Aggiungiamo o aggiorniamo le quote
                console.log(
                  `Trovate ${marketOdds.length} quote da aggiungere per l'evento ID ${responseEvent.id}`
                );
                marketOdds.forEach(newOdd => {
                  // Cerchiamo se l'odd già esiste
                  const existingOddIndex = existingEvent.odds.findIndex(
                    odd =>
                      odd.marketName === newOdd.marketName &&
                      odd.specifier === newOdd.specifier
                  );

                  if (existingOddIndex !== -1) {
                    // Aggiorna l'odd esistente in modo reattivo
                    console.log(`Aggiorno odd esistente: ${newOdd.marketName}`);
                    this.$set(existingEvent.odds, existingOddIndex, newOdd);
                  } else {
                    // Aggiungi il nuovo odd
                    console.log(`Aggiungo nuovo odd: ${newOdd.marketName}`);
                    existingEvent.odds.push(newOdd);
                  }
                });
              }
            });
          });

          return response;
        } else {
          throw new Error(
            'Errore nel caricamento delle quote: formato non valido'
          );
        }
      } catch (err) {
        console.error('Errore nella chiamata API odds:', err);
        throw err;
      }
    }, */

    /*  async fetchMarketGroups() {
      try {
        console.log(
          `Caricamento market groups per il torneo ${this.tournamentId}...`
        );
        const api = useApiV3();
        const response = await api.getMarketGroupsForTournament(
          this.sportId,
          this.categoryId,
          this.tournamentId
        );

        if (response && Array.isArray(response) && response.length > 0) {
          console.log('Market groups caricati:', response);
          this.marketGroups = response;
          // set iniziali sicuri
          const firstGroup = this.marketGroups[0];
          this.activeMarketGroup = firstGroup?._id || null;
          this.activeMarket = firstGroup?.markets?.[0]?.id || null;
          return response;
        } else {
          console.warn(
            'Market groups vuoti o non validi per questo torneo. Disabilito le tab dei mercati.'
          );
          this.marketGroups = [];
          this.activeMarketGroup = null;
          this.activeMarket = null;
          return [];
        }
      } catch (err) {
        console.error('Errore nella chiamata API market groups:', err);
        throw err;
      }
    }, */

    handleSpreadChange(newSpread: GenericOption) {
      this.activeSpread = newSpread.value;
    },

    getOutcomeNamesForEventGroup(eventGroup: PreviewEventGroup): string[] {
      // Prendi il primo evento del gruppo
      const firstEvent = eventGroup.events[0];

      // Cerca il mercato 1X2 o simili
      const market1X2 = firstEvent.odds.find(
        odd =>
          odd.marketName === '1X2' ||
          odd.marketName === 'Match Odds' ||
          odd.marketName === 'Match Winner'
      );

      // Se troviamo il mercato 1X2, usiamo i suoi outcome_name
      if (market1X2 && market1X2.outcome && market1X2.outcome.length > 0) {
        // Ordina gli outcome per il campo sort
        const sortedOutcomes = [...market1X2.outcome].sort(
          (a, b) => (a.sort || 0) - (b.sort || 0)
        );

        // Estrai i nomi degli outcome
        return sortedOutcomes.map(
          outcome => outcome.outcome_name || outcome.name
        );
      }

      // Se non troviamo il mercato 1X2, usiamo il primo mercato disponibile
      const firstMarket = firstEvent.odds[0];
      if (
        firstMarket &&
        firstMarket.outcome &&
        firstMarket.outcome.length > 0
      ) {
        // Ordina gli outcome per il campo sort
        const sortedOutcomes = [...firstMarket.outcome].sort(
          (a, b) => (a.sort || 0) - (b.sort || 0)
        );

        // Estrai i nomi degli outcome
        return sortedOutcomes.map(
          outcome => outcome.outcome_name || outcome.name
        );
      }

      return [];
    },
    setMarketAndMarketGroup(marketId: string) {
      this.selectedMarket = marketId;
      this.selectedMarketGroup = this.marketGroups.find(group =>
        group.markets.some(market => market.id === marketId)
      )?._id;
      this.showMarketMenu = false;
    },

    async loadTournamentForCategory(categoryID: string) {
      this.isTitleMenuLoading = true;
      await this.$store.dispatch(
        'IllinoisSport/fetchTournamentsForCategoryIfNeeded',
        { sportId: this.sportId, categoryId: categoryID }
      );

      this.isTitleMenuLoading = false;
    },
    getSpreadList(event: PreviewEvent): GenericOption[] {
      console.log('event', event);
      if (
        !event?.odds ||
        !Array.isArray(event.odds) ||
        event.odds.length === 0
      ) {
        return [];
      }

      // Trova tutte le specifier degli outcome nei mercati con odd_with_specifier true
      const spreadSet = new Set<string>();
      let marketName = '';

      event.odds.forEach((odd: any) => {
        if (
          odd?.odd_with_specifier &&
          odd?.outcome &&
          Array.isArray(odd.outcome)
        ) {
          // Prendi il marketName dal primo odd con odd_with_specifier
          if (!marketName && odd.marketName) {
            marketName = odd.marketName;
          }
          odd.outcome.forEach((outcome: any) => {
            if (outcome?.specifier) {
              spreadSet.add(outcome.specifier);
            }
          });
        }
      });

      // Trasforma gli specifier in GenericOption[]
      return Array.from(spreadSet).map(specifier => ({
        label: specifier,
        value: specifier
      }));
    }

    /*  async loadCategoriesForSport() {
      this.isTitleMenuLoading = true;
      const api = useApiV3();
      const response: CategoriesApiResponse = await api.getCategoriesForSport(
        this.sportId.toString()
      );
      this.categories = response.categories;
      this.isTitleMenuLoading = false;
    } */
  }
});
